import { useEffect, useRef } from 'react';

const SPEED_PX_PER_SECOND = 30;
const HOLD_AT_END_MS = 6000;
const RESUME_AFTER_MANUAL_SCROLL_MS = 10000;
const MAX_FRAME_SECONDS = 0.1;
const MOVED_BY_SOMEONE_ELSE_PX = 2;

/** The element that scrolls the page. The stylesheet makes this the body, not the window. */
function pageScroller() {
  const { body, documentElement } = document;
  return body.scrollHeight - body.clientHeight > 1 ? body : documentElement;
}

/** Where a pass starts: at the top, held long enough to read the first screen. */
export function startAutoScroll(now, pos = 0) {
  return { pos, direction: 1, holdUntil: now + HOLD_AT_END_MS, reachedBottom: false };
}

/**
 * One frame of auto scroll. Goes down, holds, comes back up, holds. If the page
 * has moved other than by this code (a person scrolling), it carries on from
 * there after a pause.
 * @param {{pos, direction, holdUntil, reachedBottom}} state
 * @param {{now: number, seconds: number, scrollY: number, max: number}} frame
 */
export function stepAutoScroll(state, { now, seconds, scrollY, max }) {
  if (max <= 1) return { ...state, pos: 0, direction: 1 };

  let { pos, holdUntil } = state;
  if (Math.abs(scrollY - pos) > MOVED_BY_SOMEONE_ELSE_PX) {
    pos = scrollY;
    holdUntil = now + RESUME_AFTER_MANUAL_SCROLL_MS;
  }
  if (now < holdUntil) return { ...state, pos, holdUntil };

  pos += state.direction * SPEED_PX_PER_SECOND * Math.min(seconds, MAX_FRAME_SECONDS);
  if (pos >= max) return { pos: max, direction: -1, holdUntil: now + HOLD_AT_END_MS, reachedBottom: true };
  if (pos <= 0) return { pos: 0, direction: 1, holdUntil: now + HOLD_AT_END_MS, reachedBottom: false };
  return { ...state, pos, holdUntil };
}

/**
 * One frame of a continuous scroll: only ever downwards. On reaching the repeat
 * of the first dashboard it moves back by one whole loop, which looks identical.
 * @param {{pos, holdUntil}} state
 * @param {{now: number, seconds: number, scrollY: number, loopStart: number, loopLength: number, max: number}} frame
 */
export function stepContinuousScroll(state, { now, seconds, scrollY, loopStart, loopLength, max }) {
  if (loopLength <= 0 || max <= 1) return state;

  let { pos, holdUntil } = state;
  if (Math.abs(scrollY - pos) > MOVED_BY_SOMEONE_ELSE_PX) {
    pos = scrollY;
    holdUntil = now + RESUME_AFTER_MANUAL_SCROLL_MS;
  }
  if (now < holdUntil) return { ...state, pos, holdUntil };

  pos += SPEED_PX_PER_SECOND * Math.min(seconds, MAX_FRAME_SECONDS);
  // Also goes back at the very bottom, so the page can never stick there.
  if (pos >= Math.min(loopStart + loopLength, max)) pos = Math.max(0, pos - loopLength);
  return { ...state, pos, holdUntil };
}

/**
 * Scrolls the page without end while active. Stands still while a dialog is
 * open, and pauses if someone scrolls. Starts from the top when restartKey changes.
 *
 * @param {boolean} active
 * @param {*} restartKey
 * @param {{current: HTMLElement}} firstRef the first dashboard
 * @param {{current: HTMLElement}} repeatRef the first dashboard's repeat, at the end
 */
export function useContinuousScroll(active, restartKey, firstRef, repeatRef) {
  useEffect(() => {
    if (!active) return undefined;

    const overlays = document.getElementsByClassName('modal-overlay');
    pageScroller().scrollTop = 0;
    let state = startAutoScroll(performance.now());
    let last = performance.now();
    let frame;

    const tick = (now) => {
      const seconds = (now - last) / 1000;
      last = now;
      if (overlays.length === 0 && firstRef.current && repeatRef.current) {
        const page = pageScroller();
        const first = firstRef.current.getBoundingClientRect().top;
        const loopStart = first + page.scrollTop;
        const loopLength = repeatRef.current.getBoundingClientRect().top - first;
        state = stepContinuousScroll(state, {
          now, seconds, scrollY: page.scrollTop, loopStart, loopLength, max: page.scrollHeight - page.clientHeight,
        });
        page.scrollTop = state.pos;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, restartKey, firstRef, repeatRef]);
}

/**
 * Slowly scrolls the page down and up while active, so everything on a long
 * dashboard is seen on a screen nobody is touching. Starts again from the top
 * when restartKey changes.
 *
 * @param {boolean} active
 * @param {*} restartKey
 * @returns {{current: boolean}} true until a pass has shown the bottom of the page
 */
export default function useAutoScroll(active, restartKey) {
  const passInProgress = useRef(false);

  useEffect(() => {
    passInProgress.current = false;
    if (!active) return undefined;

    const overlays = document.getElementsByClassName('modal-overlay');
    pageScroller().scrollTop = 0;
    let state = startAutoScroll(performance.now());
    let last = performance.now();
    let frame;

    const tick = (now) => {
      const seconds = (now - last) / 1000;
      last = now;
      // Stands still while a breakdown or other dialog is open over the dashboard.
      if (overlays.length === 0) {
        const page = pageScroller();
        const max = page.scrollHeight - page.clientHeight;
        state = stepAutoScroll(state, { now, seconds, scrollY: page.scrollTop, max });
        if (max > 1) page.scrollTop = state.pos;
        // A pass is finished once the bottom of the page has had its time on screen.
        passInProgress.current = max > 1 && (!state.reachedBottom || now < state.holdUntil);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      passInProgress.current = false;
    };
  }, [active, restartKey]);

  return passInProgress;
}
